/**
 * PhotoWallUploadPage - Public page for dancers to upload photos
 * Accessed via QR code scan
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, Upload, Send, CheckCircle, Loader2, Image as ImageIcon,
  X, AlertCircle, User, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

export default function PhotoWallUploadPage() {
  const { clubSlug } = useParams();
  const [session, setSession] = useState(null);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  
  // Form state - now supports multiple files
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSession();
  }, [clubSlug]);

  const fetchSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clubs/${clubSlug}/photo-wall/session`);
      const data = await response.json();
      
      if (data.active && data.session) {
        setSession(data.session);
        // Fetch club info
        const clubRes = await fetch(`${API_URL}/api/clubs/${clubSlug}`);
        if (clubRes.ok) {
          const clubData = await clubRes.json();
          setClub(clubData);
        }
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Validate all files
    const validFiles = [];
    const newPreviewUrls = [];
    
    for (const file of files) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }
      // Check file size (10MB max per file)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 10MB`);
        continue;
      }
      validFiles.push(file);
      newPreviewUrls.push(URL.createObjectURL(file));
    }
    
    // Limit to 10 files total
    const combinedFiles = [...selectedFiles, ...validFiles].slice(0, 10);
    const combinedPreviews = [...previewUrls, ...newPreviewUrls].slice(0, 10);
    
    if (combinedFiles.length >= 10) {
      toast.info('Maximum 10 photos per upload');
    }
    
    setSelectedFiles(combinedFiles);
    setPreviewUrls(combinedPreviews);
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previewUrls.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    setPreviewUrls([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }
    
    setUploading(true);
    setUploadProgress({ current: 0, total: selectedFiles.length });
    
    try {
      let successCount = 0;
      
      // Upload each file sequentially
      for (let i = 0; i < selectedFiles.length; i++) {
        setUploadProgress({ current: i + 1, total: selectedFiles.length });
        
        const formData = new FormData();
        formData.append('uploader_name', name.trim());
        // Only include message on the first photo
        formData.append('message', i === 0 ? message.trim() : '');
        formData.append('photo', selectedFiles[i]);
        
        const response = await fetch(`${API_URL}/api/clubs/${clubSlug}/photo-wall/photos`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          successCount++;
        } else {
          console.error(`Failed to upload photo ${i + 1}`);
        }
      }
      
      if (successCount > 0) {
        setUploaded(true);
        toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded successfully!`);
        
        // Reset form after delay
        setTimeout(() => {
          setUploaded(false);
          setName('');
          setMessage('');
          clearFiles();
        }, 3000);
      } else {
        toast.error('Failed to upload photos');
      }
    } catch (error) {
      toast.error('Error uploading photos');
      console.error(error);
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-white animate-spin" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">No Active Session</h1>
          <p className="text-slate-400">The photo wall is not currently active for this venue.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            {club?.image_url || club?.logo_url ? (
              <img 
                src={club.logo_url || club.image_url} 
                alt={club.name}
                className="w-12 h-12 rounded-xl object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold text-white">{club?.name || 'Photo Wall'}</h1>
              <p className="text-sm text-white/70">{session.session_name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-lg">
        <AnimatePresence mode="wait">
          {uploaded ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="text-center py-12"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.1 }}
                className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-6"
              >
                <CheckCircle className="w-12 h-12 text-white" />
              </motion.div>
              <h2 className="text-2xl font-bold text-white mb-2">Photos Uploaded!</h2>
              <p className="text-white/70">Your photos will appear on the slideshow shortly.</p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Photo Upload Area */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Share Your Photos
                </h2>
                
                {/* File input - always visible for adding more */}
                <label 
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/30 rounded-xl cursor-pointer hover:bg-white/5 transition-colors mb-4"
                >
                  <ImageIcon className="w-10 h-10 text-white/50 mb-2" />
                  <span className="text-white/70 text-sm">Tap to select photos</span>
                  <span className="text-white/50 text-xs mt-1">Select multiple • Max 10 photos • 10MB each</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
                
                {/* Preview Grid */}
                {previewUrls.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-white/70 text-sm">{selectedFiles.length} photo{selectedFiles.length > 1 ? 's' : ''} selected</span>
                      <button
                        type="button"
                        onClick={clearFiles}
                        className="text-white/50 hover:text-white text-xs"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {previewUrls.map((url, index) => (
                        <div key={index} className="relative aspect-square">
                          <img 
                            src={url} 
                            alt={`Preview ${index + 1}`} 
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-black/60 hover:bg-red-500 rounded-full flex items-center justify-center text-white transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Name Input */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <label className="block text-white/90 text-sm font-medium mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Your Name *
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  maxLength={50}
                  data-testid="uploader-name-input"
                />
              </div>

              {/* Message Input */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <label className="block text-white/90 text-sm font-medium mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Message (Optional)
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a caption or message..."
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 resize-none"
                  rows={3}
                  maxLength={200}
                />
                <p className="text-xs text-white/50 mt-2">{message.length}/200 characters</p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={uploading || selectedFiles.length === 0 || !name.trim()}
                className="w-full h-14 bg-white text-purple-600 hover:bg-white/90 font-semibold text-lg shadow-lg"
                data-testid="upload-photo-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Uploading {uploadProgress.current}/{uploadProgress.total}...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Share {selectedFiles.length > 1 ? `${selectedFiles.length} Photos` : 'Photo'}
                  </>
                )}
              </Button>

              <p className="text-center text-white/50 text-xs">
                Photos are reviewed before appearing on the slideshow
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
